import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Typography } from '@mui/material';
import api, { safeReturnTo } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import AuthShell from './AuthShell';

export default function AuthCallback() {
  const ran = useRef(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { completeLogin } = useAuth();

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const error = params.get('error');
    if (error) {
      const desc = params.get('error_description') || error;
      navigate(`/auth/error?reason=authentik&detail=${encodeURIComponent(desc)}`, { replace: true });
      return;
    }

    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) {
      navigate('/auth/error?reason=missing_code', { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post(
          '/api/auth/exchange',
          { code, state },
          { skipAuthRefresh: true },
        );
        completeLogin(data.access_token, data.user);
        navigate(safeReturnTo(data.return_to) || '/', { replace: true });
      } catch (e) {
        const status = e.response?.status;
        if (status === 403 || status === 409) {
          navigate(`/auth/error?reason=${status}`, { replace: true });
          return;
        }
        const detail = e.response?.data?.detail || e.message || 'exchange';
        navigate(`/auth/error?reason=exchange&detail=${encodeURIComponent(String(detail))}`, { replace: true });
      }
    })();
  }, [completeLogin, navigate, params]);

  return (
    <AuthShell subtitle="Completing sign-in">
      <CircularProgress size={28} />
      <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
        Signing you in…
      </Typography>
    </AuthShell>
  );
}